<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <p v-if="loading && !item" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadingFailed" class="text-muted">{{ _('This item is no longer available.') }}</p>
      <template v-if="item">
        <div class="max-spec-title">
          <h3>{{ item.title }}</h3>
          <span class="label" :class="statusClass(item)">{{ statusLabel(item) }}</span>
        </div>

        <div v-if="!item.valid" class="alert alert-danger">
          <p>{{ _('The stored definition is no longer valid and will not run:') }}</p>
          <ul class="max-spec-issues">
            <li v-for="(issue, i) in item.issues.slice(0, 5)" :key="i"><code>{{ issue.path }}</code> {{ issue.message }}</li>
          </ul>
        </div>
        <div v-else-if="item.can_approve" class="alert alert-warning max-spec-approval">
          <p v-if="isTool">{{ _('This tool needs your approval before anyone can use it. Check what it reads and what it can change, and try it out first: while you try it, every change asks for confirmation.') }}</p>
          <p v-else-if="isDashboard">{{ _('This dashboard needs your approval before anyone can open it. Check what it reads.') }}</p>
          <p v-else>{{ _('This report needs your approval before it runs. Check what it reads and who it emails.') }}</p>
          <div class="max-spec-actions">
            <button v-if="isTool && item.status === 'draft'" type="button" class="btn btn-default" v-on:click="openTool"><i class="fa fa-fw fa-flask"></i> {{ _('Try out') }}</button>
            <button type="button" class="btn btn-success" :disabled="saving" v-on:click="act('approve')"><i class="fa fa-fw fa-check"></i> {{ _('Approve and activate') }}</button>
          </div>
        </div>
        <div v-else-if="!item.approved && item.status !== 'archived' && isTool" class="alert alert-info">
          {{ item.approval_level >= 7 ? _('A shop administrator must approve this tool before it can be used.') : _('This tool must be approved before it can be used.') }}
        </div>
        <div v-else-if="!item.approved && item.status !== 'archived' && isDashboard" class="alert alert-info">
          {{ item.approval_level >= 7 ? _('A shop administrator must approve this dashboard before it can be opened.') : _('This dashboard must be approved before it can be opened.') }}
        </div>
        <div v-else-if="!item.approved && item.status !== 'archived'" class="alert alert-info">
          {{ item.approval_level >= 7 ? _('A shop administrator must approve this report before it runs.') : _('This report must be approved before it runs.') }}
        </div>
        <p v-else-if="item.approved && item.approver" class="text-muted">
          {{ _('Approved by %s').replace('%s', item.approver) }}<template v-if="item.approved_ts"> · {{ $filters.timestampToDate(item.approved_ts) }}</template>
        </p>
        <div v-if="canPlace && offerPlace" class="alert alert-success max-spec-approval">
          <p>{{ isDashboard ? _('The dashboard is active. Add it to a menu section so people can open it from there.') : _('The tool is active. Add it to a menu section so people can open it from there.') }}</p>
          <div class="max-spec-actions">
            <button type="button" class="btn btn-success" v-on:click="openPlace"><i class="fa fa-fw fa-plus"></i> {{ _('Add to user interface') }}</button>
          </div>
        </div>
        <p v-if="placementSummary" class="text-muted">{{ placementSummary }}</p>

        <div v-if="item.last_error" class="alert alert-danger">
          {{ _('The last run failed') }}: {{ item.last_error }}
        </div>
        <div v-if="canRun || canPreview || canPlace || item.last_artifact_id" class="max-spec-actions max-spec-run">
          <button v-if="canRun && item.kind === 'dashboard'" type="button" class="btn btn-primary" v-on:click="openDashboard">
            <i class="fa fa-fw fa-gauge" aria-hidden="true"></i> {{ _('Open dashboard') }}
          </button>
          <button v-else-if="canRun && isTool" type="button" class="btn btn-primary" v-on:click="openTool">
            <i class="fa fa-fw fa-screwdriver-wrench" aria-hidden="true"></i> {{ _('Open tool') }}
          </button>
          <button v-if="canPlace && !offerPlace" type="button" class="btn btn-default" v-on:click="openPlace">
            <i class="fa fa-fw fa-plus" aria-hidden="true"></i> {{ _('Add to user interface') }}
          </button>
          <button v-if="canRun && !isTool && !isDashboard" type="button" class="btn btn-primary" :disabled="running" v-on:click="runNow">
            <i class="fa fa-fw" :class="running ? 'fa-cog fa-spin' : 'fa-play'" aria-hidden="true"></i> {{ running ? _('Running...') : _('Run now') }}
          </button>
          <button v-if="canPreview" type="button" class="btn btn-default" :disabled="previewing" v-on:click="preview">
            <i class="fa fa-fw" :class="previewing ? 'fa-cog fa-spin' : 'fa-eye'" aria-hidden="true"></i> {{ previewing ? _('Creating preview...') : _('Preview') }}
          </button>
          <button v-if="item.last_artifact_id" type="button" class="btn btn-default" v-on:click="openArtifact(item.last_artifact_id)">
            <i class="fa fa-fw fa-file-lines" aria-hidden="true"></i> {{ _('Open the latest report') }}
          </button>
          <small v-if="item.last_run_ts" class="text-muted">{{ _('Last run') }}: {{ $filters.timestampToDate(item.last_run_ts) }}</small>
        </div>

        <div v-if="previewError" class="alert alert-danger" role="alert">
          {{ _('The preview could not be created') }}: {{ previewError }}
        </div>
        <section v-if="previewDoc" class="max-spec-section max-spec-preview" :aria-label="_('Preview')">
          <div class="flex-row space-between max-spec-preview__bar">
            <small class="text-muted"><i class="fa fa-fw fa-eye" aria-hidden="true"></i> {{ _('Preview with your access. Nothing was saved and no email was sent.') }}</small>
            <button type="button" class="btn btn-default btn-sm" v-on:click="closePreview"><i class="fa fa-fw fa-close" aria-hidden="true"></i> {{ _('Close preview') }}</button>
          </div>
          <MaxDocument :doc="previewDoc" :labels="maxLabels" />
        </section>

        <div v-if="description && description.personalData" class="alert alert-info">
          <i class="fa fa-fw fa-user-shield" aria-hidden="true"></i> {{ _('This includes personal data about customers. Only shop administrators can view the results.') }}
        </div>

        <template v-if="description">
          <section v-if="description.description" class="max-spec-section">
            <h4>{{ _('Description') }}</h4>
            <p class="max-spec-prose">{{ description.description }}</p>
          </section>

          <section v-if="isTool" class="max-spec-section">
            <h4>{{ _('What it can do') }}</h4>
            <p v-if="!description.operations.length" class="text-muted">{{ _('Nothing. The tool only shows fixed text.') }}</p>
            <ul v-else class="max-spec-list max-spec-operations">
              <li v-for="op in description.operations" :key="op.name">
                <i class="fa fa-fw" :class="op.mode === 'read' ? 'fa-eye text-muted' : 'fa-pen-to-square max-spec-flag--egress'" aria-hidden="true"></i>
                <strong>{{ op.label || operationLabel(op.operation) }}</strong>
                <span v-if="op.label" class="text-muted"> · {{ operationLabel(op.operation) }}</span>
                <span v-if="op.mode !== 'read'" class="max-spec-flag max-spec-flag--egress"> · {{ _('Changes data') }}</span>
                <span v-if="op.mode !== 'read'" class="max-spec-filter text-muted">{{ confirmText(op) }}</span>
                <span v-if="!op.calledFrom.length" class="max-spec-filter text-muted">{{ _('Not used by any button or field') }}</span>
                <span v-for="origin in op.calledFrom" :key="origin" class="max-spec-filter text-muted">{{ originText(origin) }}</span>
              </li>
            </ul>
            <p v-if="description.risk === 'write'" class="text-muted">
              <small>{{ _('Once approved, changes are saved the way the regular POS screens save them, with the same rules. Every change is recorded with the user\'s name.') }}</small>
            </p>
            <p class="max-spec-capabilities">
              <span v-for="capability in description.capabilities" :key="capability.id" class="max-spec-flag" :class="{ 'max-spec-flag--egress': capability.risk !== 'read' }">
                <i class="fa fa-fw" :class="capability.risk === 'write' ? 'fa-pen-to-square' : 'fa-eye'" aria-hidden="true"></i> {{ capabilityLabel(capability.id) }}
              </span>
            </p>
          </section>

          <section v-else class="max-spec-section">
            <h4>{{ _('What it reads') }}</h4>
            <p v-if="!description.sources.length" class="text-muted">{{ isDashboard ? _('Nothing. The dashboard only shows fixed text.') : _('Nothing. The report only shows fixed text.') }}</p>
            <ul v-else class="max-spec-list">
              <li v-for="source in description.sources" :key="source.id">
                <strong>{{ sourceName(source) }}</strong> · {{ periodLabel(source.period) }}
                <span v-if="source.maxAgeMinutes" class="text-muted"> · {{ source.live ? _('Fetched when viewed') : _('Refreshed when older than %s min').replace('%s', source.maxAgeMinutes) }}</span>
                <span v-if="source.personal" class="max-spec-flag"><i class="fa fa-fw fa-user-shield" aria-hidden="true"></i> {{ _('Personal data') }}</span>
                <span v-for="filter in source.filters" :key="filter.key" class="max-spec-filter text-muted">{{ filterText(filter) }}</span>
              </li>
            </ul>
            <p class="max-spec-capabilities">
              <span v-for="capability in description.capabilities" :key="capability.id" class="max-spec-flag" :class="{ 'max-spec-flag--egress': capability.risk !== 'read' }">
                <i class="fa fa-fw" :class="capability.risk !== 'read' ? 'fa-envelope' : 'fa-eye'" aria-hidden="true"></i> {{ capabilityLabel(capability.id) }}
              </span>
            </p>
          </section>

          <section v-if="description.schedule || (!isTool && !isDashboard)" class="max-spec-section">
            <h4>{{ _('When it runs') }}</h4>
            <p v-if="description.schedule">
              {{ scheduleSummary(description.schedule) }}<br />
              <small class="text-muted">{{ _('Times may shift by a few minutes to spread the load.') }}</small>
            </p>
            <p v-else>{{ _('Only on request') }}</p>
          </section>

          <section v-if="item.recipients.length" class="max-spec-section">
            <h4>{{ _('Who gets an email') }}</h4>
            <ul class="max-spec-list">
              <li v-for="recipient in item.recipients" :key="recipient.id">{{ recipient.name || ('#' + recipient.id) }}</li>
            </ul>
            <p v-if="description.attachment" class="text-warning">
              <i class="fa fa-paperclip" aria-hidden="true"></i>
              {{ _('The email includes the whole report as a PDF attachment. Once sent, the copy cannot be recalled or deleted by Johku.') }}
            </p>
            <small v-else class="text-muted">{{ _('The email only says the report is ready and links here. It contains no report data.') }}</small>
          </section>

          <section class="max-spec-section">
            <h4>{{ _('What it shows') }}</h4>
            <ul class="max-spec-outline">
              <li v-for="(node, i) in description.outline" :key="i" :style="{ paddingInlineStart: (node.depth * 20) + 'px' }">
                <i class="fa fa-fw text-muted" :class="nodeType(node.type).icon" :title="nodeType(node.type).label" aria-hidden="true"></i>
                <span class="sr-only">{{ nodeType(node.type).label }}:</span>
                <span v-if="node.label">{{ node.label }}</span>
                <span v-else class="text-muted">{{ nodeType(node.type).label }}</span>
                <small v-if="node.conditional" class="text-muted"> · {{ _('Shown only when a condition is met') }}</small>
                <small v-if="node.rules" class="text-muted"> · {{ _('Highlights values by rule') }}</small>
              </li>
            </ul>
          </section>
        </template>

        <section v-if="isTool && userLevel >= 7" class="max-spec-section">
          <h4>{{ _('Changes and refused requests') }}</h4>
          <button v-if="!history" type="button" class="btn btn-default btn-sm" :disabled="historyLoading" v-on:click="loadHistory">
            <i class="fa fa-fw" :class="historyLoading ? 'fa-cog fa-spin' : 'fa-clock-rotate-left'" aria-hidden="true"></i> {{ _('Show history') }}
          </button>
          <p v-else-if="!historyRows.length" class="text-muted">{{ _('No changes yet.') }}</p>
          <div v-else class="table-responsive">
            <table class="table table-condensed max-spec-history">
              <thead>
                <tr>
                  <th scope="col">{{ _('Time') }}</th>
                  <th scope="col">{{ _('User') }}</th>
                  <th scope="col">{{ _('Operation') }}</th>
                  <th scope="col">{{ _('Result') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="call in historyRows" :key="call.id">
                  <td class="text-nowrap">{{ $filters.timestampToDate(call.ts) }}</td>
                  <td>{{ call.user || ('#' + call.user_id) }}</td>
                  <td>{{ call.operation ? operationLabel(call.operation) : call.name }}</td>
                  <td :class="{ 'text-danger': call.status !== 'ok' }">
                    {{ historyStatus(call) }}
                    <small v-if="call.detail && call.detail.reason" class="text-muted max-spec-filter">{{ call.detail.reason }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <p class="text-muted max-spec-meta">
          {{ _('Revision %s').replace('%s', item.revision) }}
          <template v-if="item.author"> · {{ _('Created by') }}: {{ item.author }}</template>
          · {{ $filters.timestampToDate(item.updated_ts) }}
        </p>
      </template>
      <div class="flex-row space-between max-spec-footer">
        <span v-if="item && item.status !== 'archived'" class="max-spec-actions">
          <button v-if="item.status === 'paused'" type="button" class="btn btn-default" :disabled="saving" v-on:click="act('resume')"><i class="fa fa-fw fa-play"></i> {{ _('Resume') }}</button>
          <button v-else type="button" class="btn btn-default" :disabled="saving" v-on:click="act('pause')"><i class="fa fa-fw fa-pause"></i> {{ _('Pause') }}</button>
          <button type="button" class="btn btn-default" :disabled="saving" v-on:click="archive"><i class="fa fa-fw fa-box-archive"></i> {{ _('Archive') }}</button>
        </span>
        <span v-else></span>
        <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxlabels from '~/lib/maxlabels'
import maxspecs from '~/lib/maxspecs'
import permissions from '~/lib/permissions'
import reporttypes from '~/lib/reporttypes'
import ModalMaxSpecPlace from '~/components/modals/ModalMaxSpecPlace.vue'
import { describeSpec } from '#max-core'

export default {
  data () {
    return {
      item: null,
      running: false,
      requestId: null,
      pollGeneration: 0,
      previewing: false,
      previewDoc: null,
      previewError: null,
      previewGeneration: 0,
      history: null,
      historyLoading: false,
      offerPlace: false
    }
  },
  mixins: [components, maxlabels, maxspecs, permissions, reporttypes],
  computed: {
    canPreview () {
      return !!this.item && this.item.valid && !this.isTool && this.item.status !== 'archived' && this.userLevel >= 3
    },
    specId () {
      return this.component && this.component.initData ? parseInt(this.component.initData.specId, 10) : null
    },
    canRun () {
      return !!this.item && this.item.valid && this.item.approved && this.item.status === 'active'
    },
    description () {
      return this.item && this.item.valid && this.item.spec ? describeSpec(this.item.spec) : null
    },
    isTool () {
      return !!this.item && this.item.kind === 'interface'
    },
    isDashboard () {
      return !!this.item && this.item.kind === 'dashboard'
    },
    canPlace () {
      return !!this.item && this.item.can_place && this.canModifyLayouts
    },
    placementSummary () {
      const placements = this.item && this.item.placements ? this.item.placements : []
      if (!placements.length) {
        return ''
      }
      const names = placements.map(placement => placement.layout_name).filter(Boolean)
      if (!names.length) {
        return ''
      }
      return this._('Already on: %s').replace('%s', names.join(', '))
    },
    historyRows () {
      return (this.history || []).filter(call => (call.stage !== 'read' && call.stage !== 'background') || call.status !== 'ok')
    }
  },
  watch: {
    specId (id) {
      if (id && (!this.item || this.item.id !== id)) {
        this.offerPlace = false
        this.closePreview()
        this.loadSpec(id)
      }
    }
  },
  methods: {
    async loadSpec (id) {
      this.history = null
      this.loading = true
      this.loadingFailed = false
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/maxspecs/' + id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.loading = false
        this.loadingFailed = true
        this.item = null
        return
      }
      this.loading = false
      this.item = response.data
    },
    async act (action) {
      this.saving = true
      let response
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/maxspecs/' + this.item.id + '.json?apikey=' + this.apiKey, {
          id: this.item.id,
          action,
          spec_hash: this.item.spec_hash
        })
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      this.saving = false
      this.item = response.data
      this.EventBus.$emit('maxSpecUpdate', { id: this.item.id })
      if (action === 'approve') {
        this.showNotification(this._('Approved. It is now active.'))
        this.offerPlace = this.canPlace
      }
    },
    archive () {
      const message = this.isTool
        ? this._('Archive this tool? It cannot be edited any more.')
        : this.isDashboard
          ? this._('Archive this dashboard? It cannot be edited any more.')
          : this._('Archive this report? It stops running and cannot be edited any more.')
      if (!window.confirm(message)) {
        return
      }
      return this.act('archive')
    },
    makeRequestId () {
      const alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
      let id = ''
      for (let i = 0; i < 8; i++) {
        id += alphabet.charAt(Math.floor(Math.random() * alphabet.length))
      }
      return id
    },
    async runNow () {
      if (this.running) {
        return
      }
      this.requestId = this.makeRequestId()
      const requestId = this.requestId
      const generation = ++this.pollGeneration
      this.running = true
      try {
        await axios.post(this.baseDomain + this.shopId + '/scheduledreports.json?apikey=' + this.apiKey, {
          requestid: requestId,
          type: 'max',
          time: 'now',
          spec_id: this.item.id
        })
      } catch (e) {
        this.running = false
        return this.showError(e)
      }
      this.pollJobStatus(requestId, generation)
    },
    async pollJobStatus (requestId, generation) {
      for (let i = 0; i < 100; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000))
        if (generation !== this.pollGeneration || !this.running) {
          return
        }
        try {
          const response = await axios.get(this.baseDomain + this.shopId + '/jobstatus/' + requestId + '.json?apikey=' + this.apiKey)
          const rows = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : [])
          if (rows.find(r => r.status === 'done' || r.status === 'fail')) {
            return this.finishRun()
          }
        } catch (e) {}
      }
      if (generation === this.pollGeneration && this.running) {
        this.running = false
        this.showNotification(this._('The report is still running. Check back later.'))
      }
    },
    async finishRun () {
      if (!this.running) {
        return
      }
      this.running = false
      const previousArtifact = this.item.last_artifact_id
      await this.loadSpec(this.item.id)
      this.EventBus.$emit('maxSpecUpdate', { id: this.item.id })
      this.EventBus.$emit('maxArtifactUpdate', {})
      if (this.item.last_error) {
        return
      }
      if (this.item.last_artifact_id && this.item.last_artifact_id !== previousArtifact) {
        this.openArtifact(this.item.last_artifact_id)
      }
    },
    async preview () {
      if (this.previewing) {
        return
      }
      const requestId = this.makeRequestId()
      const generation = ++this.previewGeneration
      this.previewing = true
      this.previewDoc = null
      this.previewError = null
      try {
        await axios.post(this.baseDomain + this.shopId + '/scheduledreports.json?apikey=' + this.apiKey, {
          requestid: requestId,
          type: 'max',
          time: 'now',
          spec_id: this.item.id,
          persist: false
        })
      } catch (e) {
        this.previewing = false
        return this.showError(e)
      }
      for (let i = 0; i < 100; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        if (generation !== this.previewGeneration) {
          return
        }
        let rows
        try {
          const response = await axios.get(this.baseDomain + this.shopId + '/jobstatus/' + requestId + '.json?apikey=' + this.apiKey)
          rows = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : [])
        } catch (e) {
          continue
        }
        const done = rows.find(r => r.status === 'done')
        if (done) {
          return this.showPreview(done.result)
        }
        if (rows.find(r => r.status === 'fail')) {
          this.previewing = false
          this.previewError = this._('The run stopped unexpectedly. Try again later.')
          return
        }
      }
      if (generation === this.previewGeneration) {
        this.previewing = false
        this.previewError = this._('The run took too long. Try again later.')
      }
    },
    showPreview (raw) {
      this.previewing = false
      let result = raw
      if (typeof raw === 'string') {
        try {
          result = JSON.parse(raw)
        } catch (e) {
          result = null
        }
      }
      if (result && typeof result.error === 'string') {
        this.previewError = result.error
        return
      }
      const doc = result && result.data ? result.data.doc : null
      if (!doc) {
        this.previewError = this._('The run finished without a report.')
        return
      }
      this.previewDoc = doc
    },
    closePreview () {
      this.previewGeneration++
      this.previewing = false
      this.previewDoc = null
      this.previewError = null
    },
    openArtifact (id) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'MaxArtifactDetails',
        title: this.item.title,
        width: 8,
        data: {
          artifactId: id
        }
      })
    },
    // A tool already open in this layout (a menu tab or the details column) is brought to the
    // front instead of opening a second instance with its own state
    openTool () {
      this.EventBus.$emit('showMaxTool', {
        shopId: this.shopId,
        specId: this.item.id,
        title: this.item.title
      })
    },
    openPlace () {
      this.showModal('maxspecplace', ModalMaxSpecPlace, {
        props: {
          specId: this.item.id,
          specTitle: this.item.title,
          specKind: this.item.kind
        },
        events: {
          placed: this.afterPlace
        },
        handlers: {},
        width: 640
      })
    },
    afterPlace (placed) {
      this.offerPlace = false
      this.showNotification(this._('Added to the user interface.'))
      const clients = placed && Array.isArray(placed.clients) ? placed.clients : []
      const onThisUi = clients.some(client => client.id === this.$store.getters.clientIdNumber)
      if (onThisUi && placed.layout_id) {
        this.EventBus.$emit('gotoLayout', { id: placed.layout_id })
      }
    },
    confirmText (op) {
      if (op.confirmAbove === null || op.confirmAbove === undefined) {
        return this._('Saves without asking')
      }
      if (op.confirmAbove === 0) {
        return this._('Always asks before saving')
      }
      return this._('Asks before changing more than %s items').replace('%s', op.confirmAbove)
    },
    originText (origin) {
      if (origin === 'load') {
        return this._('When the tool opens')
      }
      const match = /^(\w+)(?:: (.*?))?(?: \/ (.*))?$/.exec(origin)
      if (!match) {
        return origin
      }
      const [, type, label, action] = match
      if (action !== undefined) {
        return this._('Row button "%s"').replace('%s', action)
      }
      if (type === 'button') {
        return this._('Button "%s"').replace('%s', label || '')
      }
      if (type === 'field') {
        return this._('When "%s" is changed').replace('%s', label || '')
      }
      if (type === 'filter') {
        return this._('When filter "%s" is changed').replace('%s', label || '')
      }
      if (type === 'datatable') {
        return label ? this._('When table "%s" is sorted or paged').replace('%s', label) : this._('When the table is sorted or paged')
      }
      if (type === 'calendar') {
        return this._('When the calendar period changes')
      }
      if (type === 'board') {
        return this._('When a card is moved on the board')
      }
      return origin
    },
    historyStatus (call) {
      switch (call.status) {
        case 'ok':
          if (call.stage === 'prepare') {
            return this._('Previewed %s changes').replace('%s', call.items)
          }
          return call.stage === 'commit' ? this._('Saved %s changes').replace('%s', call.items) : this._('Read %s rows').replace('%s', call.items)
        case 'pending':
          return this._('Saving...')
        case 'denied':
          return this._('Refused')
        case 'invalid':
          return this._('Invalid data')
        case 'rate_limited':
          return this._('Too many requests')
      }
      return this._('Failed')
    },
    async loadHistory () {
      this.historyLoading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/maxoperations.json?spec_id=' + this.item.id + '&limit=200&apikey=' + this.apiKey)
        this.history = response.data && Array.isArray(response.data.calls) ? response.data.calls : []
      } catch (e) {
        this.showError(e)
      }
      this.historyLoading = false
    },
    openDashboard () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'MaxDashboard',
        title: this.item.title,
        width: 8,
        data: {
          specId: this.item.id
        }
      })
    },
    close () {
      this.EventBus.$emit('hideDetailsColumn', {
        shopId: this.shopId
      })
    }
  },
  created () {
    if (this.specId) {
      this.loadSpec(this.specId)
    }
  },
  mounted () {
    if (!this.events) {
      this.events = {
        taskUpdate: (message) => {
          if (!message || !message.data || !this.requestId || message.data.requestid !== this.requestId) {
            return
          }
          if (message.data.status === 'done' || message.data.status === 'fail') {
            this.finishRun()
          }
        }
      }
    }
    this.enableEventBus()
  },
  beforeUnmount () {
    this.pollGeneration++
    this.previewGeneration++
  }
}
</script>

<style scoped>
.max-spec-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.max-spec-title h3 {
  text-wrap: balance;
}
.max-spec-section {
  margin-bottom: 20px;
}
.max-spec-prose {
  white-space: pre-line;
  text-wrap: pretty;
  max-width: 65ch;
}
.max-spec-list,
.max-spec-outline,
.max-spec-issues {
  padding-inline-start: 20px;
}
.max-spec-outline {
  list-style: none;
  padding-inline-start: 0;
}
.max-spec-outline li {
  padding-block: 2px;
}
.max-spec-filter {
  display: block;
  font-size: 12px;
}
.max-spec-capabilities {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}
.max-spec-flag--egress {
  color: #8a6d3b;
  font-weight: 600;
}
.max-spec-approval p {
  margin-bottom: 10px;
}
.max-spec-actions {
  display: flex;
  gap: 8px;
}
.max-spec-footer {
  margin-top: 16px;
}
.max-spec-preview__bar {
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.max-spec-run {
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
</style>
